import { createStore, select, setProp, withProps } from '@ngneat/elf';
import { Injectable } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';
import {
  getActiveEntity,
  getActiveId,
  selectActiveEntity,
  selectActiveId,
  selectAllEntities,
  setActiveId,
  setEntities,
  withActiveId,
  withEntities,
} from '@ngneat/elf-entities';
import { HttpErrorResponse } from '@angular/common/http';
import { localStorageStrategy, persistState } from '@ngneat/elf-persist-state';
import { CountryCode } from '@app-shared/enums/country-codes.enum';

import { ICountry } from './country.model';

export interface CountryProps {
  countryParam: string;
  countryCodes: string[];
  loading: boolean;
  error: HttpErrorResponse | null;
}

const initialProps: CountryProps = {
  countryParam: '',
  countryCodes: [],
  loading: false,
  error: null,
};

const STORE_NAME = 'sandoz-v2-country';

@Injectable({ providedIn: 'root' })
export class CountryRepository {
  private readonly store;

  public readonly loading$: Observable<boolean>;
  public readonly error$: Observable<HttpErrorResponse | null>;
  public readonly countryParam$: Observable<string>;
  public readonly countryCodes$: Observable<string[]>;

  public readonly countries$: Observable<ICountry[]>;
  public readonly activeCountry$: Observable<ICountry | undefined>;
  public readonly activeCountryId$;
  public readonly regionIsActive$: Observable<boolean>;

  constructor() {
    this.store = createStore(
      { name: STORE_NAME },
      withProps<CountryProps>(initialProps),
      withEntities<ICountry>(),
      withActiveId(),
    );

    persistState(this.store, {
      storage: localStorageStrategy,
      key: STORE_NAME,
    });

    this.loading$ = this.store.pipe(
      select(state => state.loading),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.error$ = this.store.pipe(
      select(state => state.error),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.countryParam$ = this.store.pipe(
      select(state => state.countryParam),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.countryCodes$ = this.store.pipe(
      select(state => state.countryCodes),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.countries$ = this.store.pipe(
      selectAllEntities(),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.activeCountry$ = this.store.pipe(
      selectActiveEntity(),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.activeCountryId$ = this.store.pipe(
      selectActiveId(),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.regionIsActive$ = this.store.pipe(
      selectActiveEntity(),
      select(
        (active: ICountry | undefined) =>
          active?.country_code === CountryCode.REGION,
      ),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  // Store operations
  resetStore(): void {
    this.store.reset();
  }

  getState(): CountryProps & {
    entities: Record<number, ICountry>;
    ids: number[];
  } & {
    activeId: number;
  } {
    return this.store.getValue();
  }

  getStoreProp<K extends keyof CountryProps>(key: K): CountryProps[K] {
    return this.store.getValue()[key];
  }

  getActiveCountry(): ICountry | null {
    return this.store.query(getActiveEntity()) || null;
  }

  getActiveId(): number | null {
    return this.store.query(getActiveId);
  }

  // Entity operations
  setCountries(countries: ICountry[]): void {
    this.store.update(setEntities(countries));
  }

  setActiveId(id: number | null): void {
    this.store.update(setActiveId(id));
  }

  // Props operations
  setLoading(loading: CountryProps['loading']): void {
    this.store.update(setProp('loading', loading));
  }

  setError(error: CountryProps['error']): void {
    this.store.update(setProp('error', error));
  }

  setCountryCodes(countries: ICountry[]): void {
    this.store.update(
      setProp(
        'countryCodes',
        countries.map(c => c.country_code),
      ),
    );
  }

  /**
   * MUST USE THIS TO SET THE ACTIVE COUNTRY.
   *
   * Updates the active country in the store.
   * Also sets the appropriate country param of the active country.
   *
   * example:
   * ```
   * input: "CH"
   * activeCountry --> Swiss country
   * countryParam --> '/ch'
   * ```
   *
   * @param countryCodeParam
   */
  setCountryByCode(countryCodeParam: string): void {
    let countryCode: string | null = null;

    if (
      countryCodeParam &&
      this.getStoreProp('countryCodes').includes(countryCodeParam.toUpperCase())
    ) {
      countryCode = countryCodeParam.toUpperCase();
    }

    let activeCountry: ICountry | null | undefined = null;

    try {
      if (countryCode) {
        activeCountry = Object.values(this.getState().entities!).find(
          c => c.country_code.toLowerCase() === countryCode!.toLowerCase(),
        );
      }
    } catch {
      activeCountry = null;
    }

    this.setActiveId(activeCountry?.id || null);

    const code = activeCountry?.country_code;
    if (code && this.getStoreProp('countryParam') !== code) {
      this.setCountryParam(code ? `/${code.toLowerCase()}` : '');
    } else if (!code && this.getStoreProp('countryParam')) {
      this.setCountryParam('');
    }
  }

  /**
   * Tries to find the country by the ID and then uses
   * the `setCountryByCode` to set the active country by the code.
   *
   * @param countryID
   */
  setActiveCountryByID(countryID: ICountry['id']): void {
    const theCountryToFind = this.getEntity(countryID);

    if (theCountryToFind?.country_code) {
      this.setCountryByCode(theCountryToFind.country_code);
    }
  }

  setCountryParam(param: string): void {
    this.store.update(setProp('countryParam', param));
  }

  getEntity(id: number): ICountry | undefined {
    return this.store.getValue().entities[id];
  }
}
